import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Mail, 
  Phone, 
  MapPin,
  Facebook, 
  Twitter, 
  Instagram,
  Trophy,
  Award,
  Users,
  ChevronDown,
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const Footer = () => {
  const { isDarkMode } = useTheme();
  const currentYear = new Date().getFullYear();
  const [openSection, setOpenSection] = useState(null);

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  // Key Features Pills
  const keyFeatures = [
    { icon: Users, text: '5,500+ Player Affiliates' },
    { icon: Trophy, text: '45 Clubs in Free State' },
    { icon: Award, text: '46 Club Affiliates' },
  ];

  
  return (
    <footer
      className={`relative overflow-hidden transition-colors duration-500 ${
        isDarkMode
          ? 'bg-fsgu-gray-900 text-fsgu-silver-100'
          : 'bg-fsgu-white text-fsgu-silver-900'
      }`}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, ${
              isDarkMode ? '#AEAEB2' : '#6D6D70'
            } 1px, transparent 0)`,
            backgroundSize: '40px 40px',
          }}
        ></div>
      </div>

      {/* Main Footer Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8">
        {/* Top Section - Logo and Description */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-6">
            <img
              src={isDarkMode ? "/fsgu-dark.png" : "/fsgu-light.png"}
              alt="FSGU Logo"
              className="h-32 lg:h-34 w-auto"
              loading="lazy"
            />
          </div>

          <p
            className={`text-lg lg:text-xl font-light leading-relaxed max-w-3xl mx-auto mb-8 ${
              isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
            }`}
          >
            Governing body for amateur golfers in the Free State province. Promoting excellence and integrity in the game of golf.
          </p>

          {/* Key Features Pills */}
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
            {keyFeatures.map((feature, index) => (
              <div
                key={index}
                className={`flex items-center space-x-2 px-4 py-2 sm:px-5 sm:py-3 rounded-full transition-colors duration-300 ${
                  isDarkMode
                    ? 'bg-fsgu-gray-800 border border-fsgu-green-500/30'
                    : 'bg-fsgu-white border border-fsgu-green-500/30'
                }`}
              >
                <feature.icon
                  className={`w-4 h-4 ${
                    isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                  }`}
                />
                <span
                  className={`text-sm font-medium ${
                    isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                  }`}
                >
                  {feature.text}
                </span>
              </div>
            ))}
          </div>
        </div>

       

        {/* Contact Section */}
        <div className="mb-12">
          <div
            className={`p-6 rounded-2xl border text-center ${
              isDarkMode
                ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                : 'bg-fsgu-silver-50 border-fsgu-silver-200'
            }`}
          >
            <h3
              className={`text-lg font-semibold mb-6 ${
                isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
              }`}
            >
              Get In Touch
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
              <a
                href="mailto:operations@fsgu.co.za"
                className={`flex flex-col items-center space-y-2 p-4 rounded-lg transition-colors duration-300 ${
                  isDarkMode
                    ? 'bg-fsgu-gray-700 hover:bg-fsgu-green-500/20'
                    : 'bg-fsgu-white hover:bg-fsgu-green-500/10'
                }`}
              >
                <Mail
                  className={`w-5 h-5 ${
                    isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                  }`}
                />
                <div>
                  <p
                    className={`text-sm font-medium ${
                      isDarkMode ? 'text-fsgu-silver-200' : 'text-fsgu-silver-900'
                    }`}
                  >
                    Email Us
                  </p>
                  <p
                    className={`text-xs ${
                      isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-600'
                    }`}
                  >
                    operations@fsgu.co.za
                  </p>
                </div>
              </a>

              <a
                href="tel:+27578991724"
                className={`flex flex-col items-center space-y-2 p-4 rounded-lg transition-colors duration-300 ${
                  isDarkMode
                    ? 'bg-fsgu-gray-700 hover:bg-fsgu-green-500/20'
                    : 'bg-fsgu-white hover:bg-fsgu-green-500/10'
                }`}
              >
                <Phone
                  className={`w-5 h-5 ${
                    isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                  }`}
                />
                <div>
                  <p
                    className={`text-sm font-medium ${
                      isDarkMode ? 'text-fsgu-silver-200' : 'text-fsgu-silver-900'
                    }`}
                  >
                    057 899 1724
                  </p>
                  <p
                    className={`text-xs ${
                      isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-600'
                    }`}
                  >
                    Call Us
                  </p>
                </div>
              </a>

              <div
                className={`flex flex-col items-center space-y-2 p-4 rounded-lg ${
                  isDarkMode
                    ? 'bg-fsgu-gray-700'
                    : 'bg-fsgu-white'
                }`}
              >
                <MapPin
                  className={`w-5 h-5 ${
                    isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                  }`}
                />
                <div>
                  <p
                    className={`text-sm font-medium ${
                      isDarkMode ? 'text-fsgu-silver-200' : 'text-fsgu-silver-900'
                    }`}
                  >
                    Free State, SA
                  </p>
                  <p
                    className={`text-xs ${
                      isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-600'
                    }`}
                  >
                    Location
                  </p>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex items-center justify-center gap-3 mt-6">
              <a
                href="https://www.facebook.com/FSGolfUnion/"
                target="_blank"
                rel="noopener noreferrer"
                className={`p-3 rounded-lg transition-all duration-300 hover:scale-110 ${
                  isDarkMode
                    ? 'bg-fsgu-gray-700 hover:bg-fsgu-green-500/20 text-fsgu-silver-400 hover:text-fsgu-green-400'
                    : 'bg-fsgu-white hover:bg-fsgu-green-500/10 text-fsgu-silver-600 hover:text-fsgu-green-500'
                }`}
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div
          className={`pt-8 border-t ${
            isDarkMode ? 'border-fsgu-gray-700' : 'border-fsgu-silver-200'
          }`}
        >
          <div className="flex flex-col items-center justify-center space-y-4">
            {/* Privacy Policy Link */}
            <div className="text-center">
              <a
                href="/GolfRSA-PAIA-POPIA-Policy-Version-1.0-UNIONS-002.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className={`text-xs transition-colors hover:underline ${
                  isDarkMode
                    ? 'text-fsgu-silver-500 hover:text-fsgu-green-400'
                    : 'text-fsgu-silver-600 hover:text-fsgu-green-500'
                }`}
              >
                Privacy Policy & POPIA Compliance
              </a>
            </div>

            {/* Copyright & Codebooth */}
            <div className="flex flex-col items-center gap-2 text-center">
              <span
                className={`text-sm ${
                  isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-600'
                }`}
              >
                © {currentYear} Free State Golf Union
              </span>
              <div className="flex items-center gap-2">
                <span
                  className={`text-sm ${
                    isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-600'
                  }`}
                >
                  Made with
                </span>
                <span className="text-green-700">❤</span>
                <span
                  className={`text-sm ${
                    isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-600'
                  }`}
                >
                  by
                </span>
                <a
                  href="https://codebooth.co.za"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-sm font-medium transition-colors hover:underline ${
                    isDarkMode
                      ? 'text-fsgu-green-400 hover:text-fsgu-green-300'
                      : 'text-fsgu-green-500 hover:text-fsgu-green-600'
                  }`}
                >
                  Codebooth
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Subtle gradient overlay at bottom */}
      <div
        className={`absolute bottom-0 left-0 right-0 h-px ${
          isDarkMode
            ? 'bg-gradient-to-r from-transparent via-fsgu-green-400/30 to-transparent'
            : 'bg-gradient-to-r from-transparent via-fsgu-green-500/30 to-transparent'
        }`}
      ></div>
    </footer>
  );
};

export default Footer;